// TIME OF DAY (owner 2026-11-02: Day / Night / Live cycle per run; night stays READABLE — brighter outlines / rim on units,
// skills light their surroundings, never pitch black). Owns the key light (sun by day, moon at night), the hemisphere fill,
// fog, the sky uniforms, the hero's light pool and the global night-readability uniforms. Follows the sim's clock
// (sim.clock / sim.night / sim.visibility); it never decides anything for the game.
import * as THREE from 'three/webgpu';
import { uniform } from 'three/tsl';
import type { DayLook, TodChoice } from '../core/mapdef';
import { lookFor, LightPreset, MapLook, presetAt, Sky } from './mapkit';
import { LIGHT_GAIN } from './fx';
import { PAINT_TINT } from './paintTint';
import { SHADOW } from './camFrame';

/** Global night-readability uniforms (enemy art reads `rim` for its rim light + outline tint). */
export const NIGHT = { rim: uniform(0), night: uniform(0) };

/** the camera distance (m) the maps' fog ranges were tuned at (the Default preset) */
const FOG_CAM_REF = 25;

export class DayLight {
  readonly hemi: THREE.HemisphereLight;
  readonly sun: THREE.DirectionalLight;
  private heroLight: THREE.PointLight | null = null;
  /** the look currently applied (main.ts reads exposure / bloom from it) */
  readonly current: LightPreset;
  private look: MapLook | null = null;
  private sky: Sky | null = null;
  private dir = new THREE.Vector3(-0.45, 0.75, -0.45).normalize();
  private fixedClock: number | null = null;

  private shadowHalf = 30;
  constructor(private scene: THREE.Scene, private shadows: boolean) {
    this.hemi = new THREE.HemisphereLight(0xd8ecff, 0x4e6a34, 1.9);
    this.sun = new THREE.DirectionalLight(0xfff0d8, 2.6);
    this.sun.position.copy(this.dir).multiplyScalar(60);
    this.sun.castShadow = shadows;
    if (shadows) {
      this.sun.shadow.mapSize.set(2048, 2048);
      const c = this.sun.shadow.camera; c.left = -30; c.right = 30; c.top = 30; c.bottom = -30; c.near = 1; c.far = 160;
      this.sun.shadow.bias = -0.0008;
    }
    scene.add(this.hemi, this.sun, this.sun.target);
    // LOAD-PERF (owner 2026-11-02 'start run takes 10–20 s'): the hero's light pool always exists (intensity 0 unless the run is
    // dark). Adding it at the first dark run changed the light set and recompiled every lit material at Start Run.
    this.heroLight = new THREE.PointLight(0xcfe0ff, 0, 15, 1.4); scene.add(this.heroLight);
    scene.fog = new THREE.Fog(0xc6ecff, 130, 520);
    this.current = { zen: 0, mid: 0, hor: 0, below: 0, sunCore: 0, sunHalo: 0, cloudLo: 0, cloudHi: 0, clouds: 1, stars: 0, moon: 0, hemiSky: 0, hemiGround: 0, hemiI: 1.9, sun: 0, sunI: 2.6,
      sunDir: [-0.45, 0.75, -0.45], fog: 0, fogNear: 130, fogFar: 520, exposure: 1, bloom: 1, rim: 0, heroLight: 0, paint: 0xffffff };
  }

  /** New map (or a new run): its look and the run's time choice. (The hero light is created up front, see the constructor.) */
  setMap(look: MapLook, sky: Sky | null, choice: TodChoice, day: DayLook): void {
    this.look = look; this.sky = sky;
    this.fixedClock = null;
    this.apply(choice === 'cycle' ? presetAt(look, 0.3, this.current) : lookFor(look, choice, day), 1, null, null);
  }
  /** QA / thumbnails: hold one clock regardless of the sim (null = follow the sim) */
  hold(clock: number | null): void { this.fixedClock = clock; }

  /** Per frame: clock + night from the sim, visibility at the hero (fog hazards), camera distance to the focus. */
  update(clock: number, visibility: number, focus: THREE.Vector3, hero: THREE.Vector3, camDist: number): void {
    if (!this.look) return;
    presetAt(this.look, this.fixedClock ?? clock, this.current);
    this.apply(this.current, visibility, focus, hero, camDist);
  }

  private apply(p: LightPreset, vis: number, focus: THREE.Vector3 | null, hero: THREE.Vector3 | null, camDist = 30): void {
    if (p !== this.current) Object.assign(this.current, p, { sunDir: [...p.sunDir] });
    this.sky?.apply(p);
    this.hemi.color.setHex(p.hemiSky); this.hemi.groundColor.setHex(p.hemiGround); this.hemi.intensity = p.hemiI;
    this.sun.color.setHex(p.sun); this.sun.intensity = p.sunI;
    this.dir.set(...p.sunDir).normalize();
    if (this.shadows && focus) { this.sun.position.copy(focus).addScaledVector(this.dir, 60); this.sun.target.position.copy(focus); }
    if (this.shadows && SHADOW.half !== this.shadowHalf) { // far camera presets: the shadow box grows with the view (camFrame.ts)
      this.shadowHalf = SHADOW.half; const c = this.sun.shadow.camera, h = SHADOW.half;
      c.left = -h; c.right = h; c.top = h; c.bottom = -h; c.updateProjectionMatrix();
    }
    else if (!this.shadows) this.sun.position.copy(this.dir).multiplyScalar(60);
    const fog = this.scene.fog as THREE.Fog;
    fog.color.setHex(p.fog);
    // fog / storm hazards: the far range closes in, but never over the hero (the camera sits camDist from it)
    const v = Math.max(0, Math.min(1, vis)), k = 1 - v;
    // the maps' fog is tuned for the Default camera (~25 m from the hero): a farther camera pushes the range out by its extra
    // distance, so the haze sits where it did around the hero instead of creeping into the frame (night fog starts at ~70 m)
    const off = Math.max(0, camDist - FOG_CAM_REF), fNear = p.fogNear + off, fFar = p.fogFar + off;
    fog.near = fNear + (camDist - 2 - fNear) * k * 1.3 * Math.min(1, k * 2);
    fog.far = fFar + (camDist + 22 - fFar) * Math.min(1, k * 1.6);
    fog.near = Math.min(fog.near, fog.far - 8);
    NIGHT.rim.value = p.rim; PAINT_TINT.value.setHex(p.paint);
    LIGHT_GAIN.k = 1 + 0.9 * p.rim;
    if (this.heroLight) {
      this.heroLight.intensity = p.heroLight; // stays in the scene at 0 (toggling visibility would recompile lit materials)
      if (hero) this.heroLight.position.set(hero.x, hero.y + 3.2, hero.z);
    }
  }
}
